import type { Birthday, User } from "@book-club/shared";
import { type RefObject, useState } from "react";
import { useUpdateMemberBirthday } from "@/features/members/mutations/useUpdateMemberBirthday";
import { useUpdateMemberDisplayName } from "@/features/members/mutations/useUpdateMemberDisplayName";
import { useUpdateMemberPhoneNumber } from "@/features/members/mutations/useUpdateMemberPhoneNumber";
import { addMemberFormSchema } from "@/features/members/schemas/addMemberForm.schema";
import { useSessionsForMonth } from "@/features/sessions/queries/useSessionsForMonth";

const displayNameSchema = addMemberFormSchema.pick({ displayName: true });
const phoneNumberSchema = addMemberFormSchema.pick({ phoneNumber: true });

type SessionMonthData = {
    year: number;
    month: number;
    sessions: NonNullable<ReturnType<typeof useSessionsForMonth>["data"]>;
};

function validateDisplayName(value: string): string | null {
    const result = displayNameSchema.safeParse({ displayName: value });
    return result.success ? null : (result.error.issues[0]?.message ?? "Invalid name");
}

function validatePhoneNumber(value: string): string | null {
    const result = phoneNumberSchema.safeParse({ phoneNumber: value });
    return result.success ? null : (result.error.issues[0]?.message ?? "Invalid phone number");
}

function birthdaysEqual(a: Birthday | null | undefined, b: Birthday | null | undefined): boolean {
    if (!a && !b) {
        return true;
    }
    if (!a || !b) {
        return false;
    }
    return a.month === b.month && a.day === b.day;
}

type ProfileFieldChanges = {
    nameChanged: boolean;
    birthdayChanged: boolean;
    phoneChanged: boolean;
};

function getProfileFieldChanges(
    member: User | null | undefined,
    name: string,
    tempBirthday: Birthday | null,
    phoneNumber: string
): ProfileFieldChanges {
    return {
        birthdayChanged: !birthdaysEqual(tempBirthday, member?.birthday),
        nameChanged: name !== member?.displayName,
        phoneChanged: phoneNumber.trim() !== (member?.phoneNumber ?? "").trim(),
    };
}

function hasAnyProfileChange(changes: ProfileFieldChanges): boolean {
    return changes.nameChanged || changes.birthdayChanged || changes.phoneChanged;
}

type ProfileInputValidation = {
    name: string;
    phoneNumber: string;
    nameError: string | null;
    phoneError: string | null;
};

/** Reads the uncontrolled name/phone inputs and validates them. */
function readAndValidateProfileInputs(
    nameInputRef: RefObject<HTMLInputElement | null>,
    phoneInputRef: RefObject<HTMLInputElement | null>
): ProfileInputValidation {
    const name = nameInputRef.current?.value?.trim() ?? "";
    const phoneNumber = phoneInputRef.current?.value ?? "";
    return {
        name,
        nameError: validateDisplayName(name),
        phoneError: validatePhoneNumber(phoneNumber),
        phoneNumber,
    };
}

type ProfileFieldValues = {
    name: string;
    birthday: Birthday | null;
    phoneNumber: string;
};

type ProfileFieldUpdaters = {
    updateDisplayName: (value: string) => Promise<unknown>;
    updateBirthday: (value: Birthday | null) => Promise<unknown>;
    updatePhoneNumber: (value: string) => Promise<unknown>;
};

async function saveChangedProfileFields(
    changes: ProfileFieldChanges,
    values: ProfileFieldValues,
    updaters: ProfileFieldUpdaters
): Promise<boolean> {
    const results = await Promise.allSettled([
        changes.nameChanged ? updaters.updateDisplayName(values.name) : Promise.resolve(),
        changes.birthdayChanged ? updaters.updateBirthday(values.birthday) : Promise.resolve(),
        changes.phoneChanged ? updaters.updatePhoneNumber(values.phoneNumber) : Promise.resolve(),
    ]);
    return results.every((result) => result.status === "fulfilled");
}

/**
 * Custom hook for MemberProfile component data, name editing, and birthday editing logic
 * Extracts session date calculations, name editing state, and birthday editing state
 */
export function useMemberProfileData(
    uid: string,
    member: User | null | undefined,
    nameInputRef: RefObject<HTMLInputElement | null>,
    phoneInputRef: RefObject<HTMLInputElement | null>
) {
    // Combined editing state for name, birthday, and phone number
    const [isEditing, setIsEditing] = useState(false);
    const [nameValidationError, setNameValidationError] = useState<string | null>(null);
    const [phoneValidationError, setPhoneValidationError] = useState<string | null>(null);
    const [tempBirthday, setTempBirthday] = useState<Birthday | null>(null);
    const { mutateAsync: updateDisplayName, isPending: isUpdatingName } = useUpdateMemberDisplayName(uid);
    const { mutateAsync: updateBirthday, isPending: isUpdatingBirthday } = useUpdateMemberBirthday(uid);
    const { mutateAsync: updatePhoneNumber, isPending: isUpdatingPhone } = useUpdateMemberPhoneNumber(uid);

    // Session date calculations
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const prevMonthDate = new Date(currentYear, currentMonth - 2, 1);
    const prevYear = prevMonthDate.getFullYear();
    const prevMonth = prevMonthDate.getMonth() + 1;

    const nextMonthDate = new Date(currentYear, currentMonth, 1);
    const nextYear = nextMonthDate.getFullYear();
    const nextMonth = nextMonthDate.getMonth() + 1;

    // Session queries
    const { data: currentMonthSessions = [] } = useSessionsForMonth(currentYear, currentMonth);
    const { data: nextMonthSessions = [] } = useSessionsForMonth(nextYear, nextMonth);
    const { data: prevMonthSessions = [] } = useSessionsForMonth(prevYear, prevMonth);

    // Start editing - opens name, birthday, and phone number for editing
    const handleStartEdit = () => {
        setIsEditing(true);
        setTempBirthday(member?.birthday ?? null);
        setNameValidationError(null);
        setPhoneValidationError(null);
    };

    // Save name, birthday, and phone number changes together
    const handleSave = async () => {
        const { name, phoneNumber, nameError, phoneError } = readAndValidateProfileInputs(nameInputRef, phoneInputRef);
        setNameValidationError(nameError);
        setPhoneValidationError(phoneError);
        if (nameError || phoneError) {
            return;
        }

        const changes = getProfileFieldChanges(member, name, tempBirthday, phoneNumber);
        if (!hasAnyProfileChange(changes)) {
            setIsEditing(false);
            return;
        }

        const allSucceeded = await saveChangedProfileFields(
            changes,
            { birthday: tempBirthday, name, phoneNumber },
            { updateBirthday, updateDisplayName, updatePhoneNumber }
        );
        if (allSucceeded) {
            setIsEditing(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setNameValidationError(null);
        setPhoneValidationError(null);
        setTempBirthday(member?.birthday ?? null);
        if (nameInputRef.current && member) {
            nameInputRef.current.value = member.displayName ?? "";
        }
    };

    const currentMonthData: SessionMonthData = {
        month: currentMonth,
        sessions: currentMonthSessions ?? [],
        year: currentYear,
    };

    const nextMonthData: SessionMonthData = {
        month: nextMonth,
        sessions: nextMonthSessions ?? [],
        year: nextYear,
    };

    const prevMonthData: SessionMonthData = {
        month: prevMonth,
        sessions: prevMonthSessions ?? [],
        year: prevYear,
    };

    return {
        handleCancelEdit,
        handleSave,
        handleStartEdit,
        isEditing,
        isUpdating: isUpdatingName || isUpdatingBirthday || isUpdatingPhone,
        nameValidationError,
        phoneValidationError,
        sessionData: {
            current: currentMonthData,
            next: nextMonthData,
            prev: prevMonthData,
        },
        setTempBirthday,
        tempBirthday,
    };
}
